import React from "react";
import {
  Box,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";

export type DiscountMode = "percentage" | "amount";

const MODES: { value: DiscountMode; label: string; symbol: string }[] = [
  { value: "percentage", label: "Percentage", symbol: "%" },
  { value: "amount", label: "Amount", symbol: "₹" },
];

interface IDiscountModeSelectorProps {
  value: DiscountMode;
  onChange: (next: DiscountMode) => void;
  disabled?: boolean;
}

/**
 * Clickable discount-mode trigger ("% ▾" / "₹ ▾") that opens a small popup to
 * pick between Percentage and Amount. Styled to sit inside a field's end
 * adornment so it reads like the field's unit but is interactive — the discount
 * counterpart of TaxTypeSelector.
 */
export const DiscountModeSelector: React.FC<IDiscountModeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const activeSymbol =
    MODES.find((m) => m.value === value)?.symbol ?? "%";

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!disabled) setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (next: DiscountMode) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    if (next !== value) onChange(next);
  };

  return (
    <>
      <Box
        component="span"
        role="button"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0,
          fontWeight: 700,
          fontSize: "0.8rem",
          lineHeight: 1,
          color: "text.secondary",
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          px: 0,
          mx: 0,
          "&:hover": disabled ? {} : { color: "text.primary" },
        }}
      >
        {activeSymbol}
        {!disabled && <ArrowDropDownIcon sx={{ fontSize: 15, ml: "-1px" }} />}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { minWidth: 140, mt: 0.5 } }}
      >
        {MODES.map((mode) => (
          <MenuItem
            key={mode.value}
            selected={mode.value === value}
            onClick={handleSelect(mode.value)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {mode.value === value && (
                <CheckIcon fontSize="small" color="primary" />
              )}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>
              {mode.label} ({mode.symbol})
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
